import { IDENTIFY_QUEUE_DB } from '@/lib/storeNames';
import { IDENTIFY_QUEUE_STORE, IDENTIFY_QUEUE_VERSION } from './identifyQueueDb';

export interface IdentifyJob {
  itemId: string;
  touchedAt: number;
  // Tells a touch during a run from the job that ran, even within one millisecond.
  touchId: string;
  nextAttemptAt: number;
  backoffStep: number;
  attempts: number;
  transientAttempts: number;
  failed: boolean;
  // Set by a timed-out upload, so later attempts send only thumbnails.
  thumbnails?: boolean;
}

export type PauseReason = 'offline' | 'signed_out' | 'rate_limited' | 'service_unavailable';

export type JobOutcome =
  | 'done'
  | 'drop'
  | 'wait'
  | { unavailable: PauseReason }
  | 'transient'
  | 'upload_timeout'
  | 'bad_output'
  | 'permanent';
export type RunJob = (itemId: string, opts?: { thumbnails?: boolean }) => Promise<JobOutcome>;

export interface JobStore {
  get(itemId: string): Promise<IdentifyJob | undefined>;
  put(job: IdentifyJob): Promise<void>;
  delete(itemId: string): Promise<void>;
  list(): Promise<IdentifyJob[]>;
}

export interface QueueSnapshot {
  pending: string[];
  failed: string[];
  nextDueAt: number | undefined;
  paused: PauseReason | undefined;
}

export interface IdentifyQueue {
  touch(itemId: string): Promise<void>;
  drain(run: RunJob): Promise<void>;
  wake(): Promise<void>;
  nudge(itemId: string): Promise<void>;
  retry(itemId: string): Promise<void>;
  refresh(): Promise<void>;
  snapshot(): QueueSnapshot;
  subscribe(listener: () => void): () => void;
}

type WithLock = (fn: () => Promise<void>) => Promise<void>;

export const WAIT_MS = 15_000;
export const BACKOFF_MS = [30_000, 120_000, 600_000, 3_600_000];
export const MAX_BAD_OUTPUT = 3;
// A job that never succeeds must end: the sixth transient attempt comes about 2 h 13 min
// after the first (30 s + 2 m + 10 m + 60 m + 60 m of backoff).
export const MAX_TRANSIENT = 6;

export function memoryJobStore(): JobStore {
  const jobs = new Map<string, IdentifyJob>();
  return {
    get: async (itemId) => {
      const job = jobs.get(itemId);
      return job && { ...job };
    },
    put: async (job) => {
      jobs.set(job.itemId, { ...job });
    },
    delete: async (itemId) => {
      jobs.delete(itemId);
    },
    list: async () => [...jobs.values()].map((j) => ({ ...j })),
  };
}

export function indexedDbJobStore(
  dbName = IDENTIFY_QUEUE_DB,
  storeName = IDENTIFY_QUEUE_STORE,
): JobStore {
  let db: Promise<IDBDatabase> | undefined;
  const open = () => {
    db ??= new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(dbName, IDENTIFY_QUEUE_VERSION);
      req.onupgradeneeded = () => {
        req.result.createObjectStore(storeName, { keyPath: 'itemId' });
      };
      req.onsuccess = () => {
        // iOS drops the connection when the app is backgrounded; reopen on the next operation.
        req.result.onclose = () => {
          db = undefined;
        };
        resolve(req.result);
      };
      req.onerror = () => reject(req.error);
    }).catch((err: unknown) => {
      db = undefined;
      throw err;
    });
    return db;
  };
  async function request<T>(mode: IDBTransactionMode, op: (s: IDBObjectStore) => IDBRequest<T>) {
    const conn = await open();
    return new Promise<T>((resolve, reject) => {
      const req = op(conn.transaction(storeName, mode).objectStore(storeName));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  return {
    get: (itemId) => request<IdentifyJob | undefined>('readonly', (s) => s.get(itemId)),
    put: async (job) => {
      await request('readwrite', (s) => s.put(job));
    },
    delete: async (itemId) => {
      await request('readwrite', (s) => s.delete(itemId));
    },
    list: () => request<IdentifyJob[]>('readonly', (s) => s.getAll()),
  };
}

// Two open tabs share the IndexedDB queue; the lock keeps them from sending the same job.
function webLock(): WithLock {
  return async (fn) => {
    if (typeof navigator === 'undefined' || !navigator.locks) {
      await fn();
      return;
    }
    await navigator.locks.request('rosa-identify-drain', async () => {
      await fn();
    });
  };
}

const dueAt = (job: IdentifyJob) => Math.max(job.touchedAt, job.nextAttemptAt);

const backoffDelay = (step: number) => BACKOFF_MS[Math.min(step, BACKOFF_MS.length - 1)] ?? 0;

function backoff(job: IdentifyJob, t: number): IdentifyJob {
  return {
    ...job,
    nextAttemptAt: t + backoffDelay(job.backoffStep),
    backoffStep: job.backoffStep + 1,
  };
}

function settle(
  job: IdentifyJob,
  outcome: Exclude<JobOutcome, object>,
  t: number,
): IdentifyJob | undefined {
  switch (outcome) {
    case 'done':
    case 'drop':
      return undefined;
    case 'wait':
      return { ...job, nextAttemptAt: t + WAIT_MS };
    case 'transient': {
      const transientAttempts = job.transientAttempts + 1;
      return transientAttempts >= MAX_TRANSIENT
        ? { ...job, transientAttempts, failed: true }
        : { ...backoff(job, t), transientAttempts };
    }
    case 'upload_timeout':
      return settle({ ...job, thumbnails: true }, 'transient', t);
    case 'bad_output': {
      const attempts = job.attempts + 1;
      return attempts >= MAX_BAD_OUTPUT
        ? { ...job, attempts, failed: true }
        : { ...backoff(job, t), attempts };
    }
    case 'permanent':
      return { ...job, failed: true };
  }
}

export function createIdentifyQueue(
  opts: { store?: JobStore; now?: () => number; withLock?: WithLock } = {},
): IdentifyQueue {
  const store = opts.store ?? defaultStore();
  const now = opts.now ?? Date.now;
  const withLock = opts.withLock ?? webLock();
  const listeners = new Set<() => void>();
  let snap: QueueSnapshot = { pending: [], failed: [], nextDueAt: undefined, paused: undefined };
  let draining = false;
  let rerun = false;
  // The runner from the latest drain call: a graph change hands in a new one mid-pass.
  let runner: RunJob | undefined;
  // In memory only: after a reload the first job probes the provider a little early.
  let pausedUntil = 0;
  let pauseReason: PauseReason | undefined;
  // Counts the provider's outage, not the job's: a job's own failures must not lengthen a pause.
  let pauseStep = 0;

  async function refresh(): Promise<void> {
    const jobs = await store.list();
    const live = jobs.filter((j) => !j.failed);
    const due = live.map(dueAt);
    snap = {
      pending: live.map((j) => j.itemId).sort(),
      failed: jobs
        .filter((j) => j.failed)
        .map((j) => j.itemId)
        .sort(),
      nextDueAt: due.length > 0 ? Math.max(Math.min(...due), pausedUntil) : undefined,
      paused: now() < pausedUntil ? pauseReason : undefined,
    };
    for (const l of listeners) l();
  }

  async function drainOnce(): Promise<void> {
    const run = runner;
    if (!run) return;
    // Earliest due first: the job that caused a pause comes due as it ends, and must not
    // re-probe ahead of the jobs it held back.
    const jobs = (await store.list()).sort(
      (a, b) => dueAt(a) - dueAt(b) || a.itemId.localeCompare(b.itemId),
    );
    for (const job of jobs) {
      // A new runner belongs to another graph: leave the rest of the pass to it.
      if (runner !== run) {
        rerun = true;
        return;
      }
      const t = now();
      if (t < pausedUntil) return;
      if (job.failed || t < job.nextAttemptAt) continue;
      let outcome: JobOutcome;
      try {
        outcome = await run(job.itemId, { thumbnails: job.thumbnails ?? false });
      } catch (err) {
        console.warn('identify job failed', job.itemId, err);
        outcome = 'transient';
      }
      const settledAt = now();
      if (typeof outcome === 'object') {
        pausedUntil = settledAt + backoffDelay(pauseStep++);
        pauseReason = outcome.unavailable;
      } else if (outcome === 'done') {
        pauseStep = 0;
      }
      // A touch during the run means newer photos: leave the fresh job for the next pass.
      const current = await store.get(job.itemId);
      if (!current || current.touchId === job.touchId) {
        const next =
          typeof outcome === 'object'
            ? { ...job, nextAttemptAt: pausedUntil }
            : settle(job, outcome, settledAt);
        if (next) await store.put(next);
        else await store.delete(job.itemId);
      }
    }
  }

  return {
    async touch(itemId) {
      await store.put({
        itemId,
        touchedAt: now(),
        touchId: crypto.randomUUID(),
        nextAttemptAt: 0,
        backoffStep: 0,
        attempts: 0,
        transientAttempts: 0,
        failed: false,
      });
      // The pass in progress listed its jobs before this one existed.
      if (draining) rerun = true;
      await refresh();
    },
    async drain(run) {
      runner = run;
      if (draining) {
        rerun = true;
        return;
      }
      draining = true;
      try {
        do {
          rerun = false;
          await withLock(drainOnce);
        } while (rerun);
      } finally {
        draining = false;
        await refresh();
      }
    },
    async wake() {
      // Under the drain lock, so a job settled mid-wake isn't overwritten by its stale copy.
      await withLock(async () => {
        pausedUntil = 0;
        pauseStep = 0;
        for (const job of await store.list()) {
          if (!job.failed && (job.nextAttemptAt > 0 || job.backoffStep > 0)) {
            await store.put({ ...job, nextAttemptAt: 0, backoffStep: 0 });
          }
        }
      });
      await refresh();
    },
    // Makes an existing job due now without starting one: photos change without the user
    // asking to identify the item, as when an upload commits.
    async nudge(itemId) {
      await withLock(async () => {
        const job = await store.get(itemId);
        if (!job) return;
        // A failed job is revived: it failed on a photo set the commit has since changed.
        const revived = { failed: false, attempts: 0, transientAttempts: 0, backoffStep: 0 };
        await store.put({ ...job, ...(job.failed ? revived : {}), nextAttemptAt: 0 });
      });
      if (draining) rerun = true;
      await refresh();
    },
    async retry(itemId) {
      const job = await store.get(itemId);
      if (!job) return;
      pausedUntil = 0;
      pauseStep = 0;
      await store.put({
        ...job,
        failed: false,
        attempts: 0,
        transientAttempts: 0,
        backoffStep: 0,
        nextAttemptAt: 0,
      });
      await refresh();
    },
    refresh,
    snapshot: () => snap,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

// jsdom has no IndexedDB; queue in memory there, as photoQueue does.
function defaultStore(): JobStore {
  return typeof globalThis.indexedDB === 'undefined' ? memoryJobStore() : indexedDbJobStore();
}

let shared: IdentifyQueue | undefined;

export function getIdentifyQueue(): IdentifyQueue {
  shared ??= createIdentifyQueue();
  return shared;
}
